/*
 * Size dropdown sorting.
 *
 * Sorts every "Size" <select> on the page smallest -> largest (by width, then
 * length): the sticky add-to-cart bar, the quick-view / choose-options popup
 * and combined-listing pickers all render sizes in the order the option values
 * were entered, which is rarely ascending.
 *
 * This module deliberately does NOT choose a variant. The selected size is
 * whatever Shopify and the theme rendered server-side
 * (product.selected_or_first_available_variant, or the ?variant= deep link),
 * so the price painted on first render is the correct one. An earlier version
 * pre-selected a merchant-configured default by dispatching a synthetic change
 * event; that made the theme fetch-and-swap the product info after load and the
 * price visibly changed a beat later. Sorting alone cannot cause that: option
 * nodes are reordered inside a closed select, the current value is restored,
 * and no event is dispatched.
 *
 * Radio/button pickers are left untouched — the theme lays those out in the
 * template and reordering them at runtime would shift the layout after paint.
 *
 * Config comes from the theme via window.Eurus.sizeSort:
 *   { enabled: Boolean, optionName: String }
 *
 * The sort re-runs on every (re)render via a MutationObserver, so it survives
 * the theme's fetch-and-swap variant updates and popup injections.
 */
(function () {
  var hasWindow = typeof window !== 'undefined';
  if (hasWindow && window.__eurusSizeSortLoaded) return;
  if (hasWindow) window.__eurusSizeSortLoaded = true;

  function cfg() {
    return (hasWindow && window.Eurus && window.Eurus.sizeSort) || null;
  }
  function isEnabled() {
    var c = cfg();
    return !!c && c.enabled !== false;
  }
  function optionName() {
    var c = cfg();
    return (c && c.optionName) || 'Size';
  }

  // Lowercase, unify the multiplication sign, and fold curly/prime quotes onto
  // plain ' and ". The marks are KEPT: they carry the unit, and stripping them
  // first is what used to turn 2'6" into "26" and sort a runner as 26 feet.
  function normSize(s) {
    return (s == null ? '' : String(s))
      .toLowerCase()
      .replace(/×/g, 'x') // × -> x
      .replace(/[‘’′]/g, "'")
      .replace(/[“”″]/g, '"');
  }

  // One dimension -> feet. Handles 8' | 6'6" | 2'6 | 12" | bare 8 / 2.5.
  function dimToFeet(token) {
    var t = token.trim();
    var m = /^(\d+(?:\.\d+)?)\s*'\s*(\d+(?:\.\d+)?)\s*"?$/.exec(t); // 6'6"
    if (m) return parseFloat(m[1]) + parseFloat(m[2]) / 12;
    m = /^(\d+(?:\.\d+)?)\s*'$/.exec(t); // 8'
    if (m) return parseFloat(m[1]);
    m = /^(\d+(?:\.\d+)?)\s*"$/.exec(t); // 12"
    if (m) return parseFloat(m[1]) / 12;
    m = /^(\d+(?:\.\d+)?)$/.exec(t); // 8
    if (m) return parseFloat(m[1]);
    return null;
  }

  // Parse "W x L" into numeric feet. Returns null unless both dims parse, so a
  // swatch ("Swatch 12\" x 12\"") stays unsorted rather than sorting as 1 x 1.
  function parseDims(value) {
    var parts = normSize(value).split('x');
    if (parts.length !== 2) return null;
    var w = dimToFeet(parts[0]);
    var l = dimToFeet(parts[1]);
    if (w === null || l === null) return null;
    return { w: w, l: l };
  }

  // Width ascending, then length ascending. Unparseable values (a swatch, a
  // "Custom" entry) sink to the end; the caller keeps their original relative
  // order for a stable result.
  function compareSizes(aVal, bVal) {
    var a = parseDims(aVal);
    var b = parseDims(bVal);
    if (a && b) return a.w !== b.w ? a.w - b.w : a.l - b.l;
    if (a) return -1;
    if (b) return 1;
    return 0;
  }

  function matchesSizeName(name) {
    if (!name) return false;
    var target = optionName().toLowerCase();
    var n = name.toLowerCase();
    // Dropdowns use the raw option name ("Size"); combined-listing pickers
    // suffix the position ("Size-1").
    return n === target || n.indexOf(target + '-') === 0;
  }

  function sortSelect(select) {
    var opts = Array.prototype.slice.call(select.options);
    if (opts.length < 2) return;
    var sorted = opts
      .map(function (el, i) {
        return { el: el, i: i };
      })
      .sort(function (A, B) {
        return compareSizes(A.el.value, B.el.value) || A.i - B.i;
      })
      .map(function (o) {
        return o.el;
      });

    var alreadySorted = opts.every(function (el, i) {
      return el === sorted[i];
    });
    if (alreadySorted) return;

    var selectedValue = select.value; // detaching options can drop the selection
    var frag = document.createDocumentFragment();
    sorted.forEach(function (el) {
      frag.appendChild(el);
    });
    select.appendChild(frag);
    if (select.value !== selectedValue) select.value = selectedValue; // restore
  }

  function process(scope) {
    if (!isEnabled()) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice.call(root.querySelectorAll('select')).forEach(function (select) {
      if (matchesSizeName(select.name)) sortSelect(select);
    });
    // A scoped node may itself be a select (e.g. injected directly).
    if (root.matches && root.matches('select') && matchesSizeName(root.name)) sortSelect(root);
  }

  function runProcess() {
    try {
      process(document);
    } catch (e) {
      /* never let selector logic break the page */
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // setTimeout (not rAF) so the pass still runs in background/inactive tabs.
    setTimeout(function () {
      scheduled = false;
      runProcess();
    }, 0);
  }

  function init() {
    runProcess(); // immediate first pass (DOM is parsed for a deferred script)
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) {
            schedule();
            return;
          }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // Exposed for unit testing under Node; harmless in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      normSize: normSize,
      dimToFeet: dimToFeet,
      parseDims: parseDims,
      compareSizes: compareSizes,
      matchesSizeName: matchesSizeName,
      sortSelect: sortSelect,
      process: process,
    };
  }
})();
